import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import SmartRichText from "@/components/SmartRichText";
import { EmailMomDialog, MomLogDialog } from "@/components/MeetingDetailInline";
import {
  Calendar, Plus, Trash2, Video, MapPin, Users, Sparkles, MessageSquare, ListChecks, Repeat,
  Lock, Globe, Loader2, ClipboardList, FileText, Wand2, GripVertical, X, Download, Mail, History,
} from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative } from "@/lib/format";

const STATUS_AGENDA = ["pending", "discussed", "skipped"];
const STATUS_MEETING = ["scheduled", "in_progress", "done", "cancelled"];
const PRIORITIES = ["low", "medium", "high", "urgent"];
const TASK_STATUSES = ["pending_approval", "open", "in_progress", "review", "blocked", "done"];

export default function ProjectMeetingsTab({ projectId, workspaceId, projectMembers = [], myPerms = [] }) {
  const canCreate = myPerms.includes("meeting.create");
  const canUpdate = myPerms.includes("meeting.update");
  const canDelete = myPerms.includes("meeting.delete");
  const canCreateTask = myPerms.includes("task.create");
  const canUpdateTask = myPerms.includes("task.update");

  const [rows, setRows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [busy, setBusy] = useState(true);
  const [openMeeting, setOpenMeeting] = useState(null);

  const load = async () => {
    setBusy(true);
    try {
      const [m, t] = await Promise.all([
        api.get(`/projects/${projectId}/meetings`),
        api.get(`/workspaces/${workspaceId}/meeting-templates`).catch(() => ({ data: [] })),
      ]);
      setRows(m.data || []);
      setTemplates(t.data || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load meetings")); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [projectId]);

  const upcoming = useMemo(() => rows.filter((r) => new Date(r.scheduled_at) >= new Date()), [rows]);
  const past = useMemo(() => rows.filter((r) => new Date(r.scheduled_at) < new Date()), [rows]);

  return (
    <div className="space-y-6" data-testid="project-meetings-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="label-eyebrow">Meetings</div>
          <p className="text-xs text-muted-foreground mt-1">Schedule and run internal & client meetings. Action items roll into the project&apos;s Tasks.</p>
        </div>
        {canCreate && (
          <NewMeetingDialog projectId={projectId} workspaceId={workspaceId} projectMembers={projectMembers} templates={templates} onSaved={load} />
        )}
      </div>

      <MeetingSection title="Upcoming" rows={upcoming} onOpen={setOpenMeeting} empty="No upcoming meetings." testid="meetings-upcoming" />
      {past.length > 0 && (
        <MeetingSection title="Past" rows={past.slice().reverse()} onOpen={setOpenMeeting} empty="" testid="meetings-past" dimmed />
      )}

      {busy && rows.length === 0 && <div className="text-sm text-muted-foreground">Loading…</div>}

      {openMeeting && (
        <MeetingDetailDialog
          meetingId={openMeeting.id}
          projectMembers={projectMembers}
          canUpdate={canUpdate}
          canDelete={canDelete}
          canCreateTask={canCreateTask}
          canUpdateTask={canUpdateTask}
          onClose={() => { setOpenMeeting(null); load(); }}
        />
      )}
    </div>
  );
}

function MeetingSection({ title, rows, onOpen, empty, testid, dimmed = false }) {
  return (
    <div data-testid={testid}>
      <div className="label-eyebrow mb-2">{title} · {rows.length}</div>
      {rows.length === 0 ? (
        empty && <div className="text-xs text-muted-foreground">{empty}</div>
      ) : (
        <div className={`border border-border ${dimmed ? "opacity-80" : ""}`}>
          <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-border label-eyebrow text-[10px] bg-muted/40">
            <div className="col-span-4">Title</div>
            <div className="col-span-2">When</div>
            <div className="col-span-2">Kind</div>
            <div className="col-span-2 text-center">Action items</div>
            <div className="col-span-2 text-right">Status</div>
          </div>
          {rows.map((m) => (
            <button
              type="button"
              key={m.id}
              onClick={() => onOpen(m)}
              className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-border items-center text-sm w-full text-left hover:bg-muted/40"
              data-testid={`meeting-row-${m.id}`}
            >
              <div className="col-span-4 min-w-0">
                <div className="font-medium truncate">{m.title}{m.series_id ? <Repeat className="inline w-3 h-3 ml-1 text-muted-foreground" /> : null}</div>
                <div className="text-xs text-muted-foreground truncate">{m.location || m.video_url || "—"}</div>
              </div>
              <div className="col-span-2 text-xs">
                <div>{new Date(m.scheduled_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}</div>
                <div className="text-muted-foreground">{m.duration_minutes || 30} min</div>
              </div>
              <div className="col-span-2">
                {m.is_external ? <span className="chip chip-client inline-flex items-center gap-1 text-[10px]"><Globe className="w-3 h-3" /> external</span> : <span className="chip chip-approved inline-flex items-center gap-1 text-[10px]"><Lock className="w-3 h-3" /> internal</span>}
              </div>
              <div className="col-span-2 text-center text-xs inline-flex justify-center items-center gap-1">
                <ListChecks className="w-3 h-3" /> {m.action_item_count || 0}
              </div>
              <div className="col-span-2 text-right">
                <span className="chip text-[10px]">{m.status?.replace("_", " ") || "scheduled"}</span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function NewMeetingDialog({ projectId, workspaceId, projectMembers, templates, onSaved }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tmpl, setTmpl] = useState("");
  const blank = {
    title: "", description: "", is_external: false,
    scheduled_at: "", duration_minutes: 30,
    location: "", video_url: "",
    attendee_user_ids: [], external_attendees: [],
    agenda: [], discussion_notes: "",
    recurrence_freq: "", recurrence_count: 12,
  };
  const [form, setForm] = useState(blank);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const applyTemplate = (tid) => {
    setTmpl(tid);
    if (!tid) return;
    const t = templates.find((x) => x.id === tid);
    if (!t) return;
    setForm((f) => ({
      ...f,
      title: t.name,
      description: t.description || f.description,
      duration_minutes: t.duration_minutes || f.duration_minutes,
      is_external: t.is_external,
      agenda: (t.agenda || []).map((a) => ({ ...a, id: undefined })),
    }));
  };

  const addAgendaRow = () => set("agenda", [...form.agenda, { title: "", duration_minutes: null, status: "pending" }]);
  const updateAgenda = (i, k, v) => set("agenda", form.agenda.map((a, idx) => idx === i ? { ...a, [k]: v } : a));
  const removeAgenda = (i) => set("agenda", form.agenda.filter((_, idx) => idx !== i));

  const toggleAttendee = (uid) => {
    set("attendee_user_ids", form.attendee_user_ids.includes(uid)
      ? form.attendee_user_ids.filter((x) => x !== uid)
      : [...form.attendee_user_ids, uid]);
  };

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    if (!form.scheduled_at) return toast.error("Pick a date & time");
    setBusy(true);
    try {
      const body = {
        title: form.title,
        description: form.description || null,
        is_external: form.is_external,
        scheduled_at: new Date(form.scheduled_at).toISOString(),
        duration_minutes: Number(form.duration_minutes) || 30,
        location: form.location || null,
        video_url: form.video_url || null,
        attendee_user_ids: form.attendee_user_ids,
        external_attendees: form.external_attendees,
        agenda: form.agenda.filter((a) => (a.title || "").trim()),
        discussion_notes: form.discussion_notes || null,
        template_id: tmpl || null,
      };
      if (form.recurrence_freq) {
        body.recurrence = { frequency: form.recurrence_freq, occurrences: Number(form.recurrence_count) || 12 };
      }
      const { data } = await api.post(`/projects/${projectId}/meetings`, body);
      toast.success(`Meeting created · ${data.occurrences_created} occurrence(s)`);
      setOpen(false); setForm(blank); setTmpl("");
      onSaved && onSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't create meeting"));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(blank); setTmpl(""); } }}>
      <DialogTrigger asChild>
        <Button size="sm" className="rounded-none" data-testid="meeting-new-btn"><Plus className="w-3.5 h-3.5 mr-1.5" /> Schedule meeting</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col" data-testid="meeting-new-dialog">
        <DialogHeader>
          <DialogTitle>Schedule meeting</DialogTitle>
          <DialogDescription>Internal team or client-facing. Use templates to standardise agendas.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 flex-1 overflow-y-auto pr-1">
          {templates.length > 0 && (
            <div>
              <div className="label-eyebrow mb-1">From template (optional)</div>
              <Select value={tmpl || "_none"} onValueChange={(v) => applyTemplate(v === "_none" ? "" : v)}>
                <SelectTrigger className="h-9 text-sm" data-testid="meeting-new-template"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— None —</SelectItem>
                  {templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div>
            <div className="label-eyebrow mb-1">Title <span className="text-amber-500">*</span></div>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="meeting-new-title" />
          </div>
          <div>
            <div className="label-eyebrow mb-1">Description</div>
            <Textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="meeting-new-desc" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <div className="label-eyebrow mb-1">When <span className="text-amber-500">*</span></div>
              <Input type="datetime-local" value={form.scheduled_at} onChange={(e) => set("scheduled_at", e.target.value)} data-testid="meeting-new-when" />
            </div>
            <div>
              <div className="label-eyebrow mb-1">Duration (min)</div>
              <Input type="number" min={5} max={480} value={form.duration_minutes} onChange={(e) => set("duration_minutes", e.target.value)} data-testid="meeting-new-duration" />
            </div>
            <div>
              <div className="label-eyebrow mb-1">Kind</div>
              <label className="flex items-center gap-2 h-9 px-3 border border-border text-xs cursor-pointer">
                <Checkbox checked={form.is_external} onCheckedChange={(v) => set("is_external", !!v)} data-testid="meeting-new-external" />
                Client-facing (external)
              </label>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="label-eyebrow mb-1"><MapPin className="w-3 h-3 inline mr-1" /> Location</div>
              <Input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder="Room 3 · Office" data-testid="meeting-new-location" />
            </div>
            <div>
              <div className="label-eyebrow mb-1"><Video className="w-3 h-3 inline mr-1" /> Video link</div>
              <Input value={form.video_url} onChange={(e) => set("video_url", e.target.value)} placeholder="https://meet.google.com/..." data-testid="meeting-new-video" />
            </div>
          </div>

          <div>
            <div className="label-eyebrow mb-1 inline-flex items-center gap-1"><Users className="w-3 h-3" /> Attendees</div>
            <div className="flex flex-wrap gap-1.5 border border-border p-2 max-h-32 overflow-y-auto">
              {projectMembers.map((m) => {
                const on = form.attendee_user_ids.includes(m.user_id);
                return (
                  <button
                    type="button"
                    key={m.user_id}
                    onClick={() => toggleAttendee(m.user_id)}
                    className={`chip text-[10px] ${on ? "chip-approved" : ""}`}
                    data-testid={`meeting-new-attendee-${m.user_id}`}
                  >
                    {m.user?.name || m.user?.email}
                  </button>
                );
              })}
              {projectMembers.length === 0 && <span className="text-xs text-muted-foreground">No project members yet — add team in the Members tab.</span>}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="label-eyebrow inline-flex items-center gap-1"><ClipboardList className="w-3 h-3" /> Agenda</div>
              <Button size="sm" variant="ghost" type="button" onClick={addAgendaRow} className="rounded-none h-7 text-[10px]" data-testid="meeting-new-agenda-add">
                <Plus className="w-3 h-3 mr-1" /> Item
              </Button>
            </div>
            <div className="space-y-1">
              {form.agenda.map((a, i) => (
                <div key={i} className="flex gap-1 items-center" data-testid={`meeting-new-agenda-${i}`}>
                  <GripVertical className="w-3 h-3 text-muted-foreground" />
                  <Input value={a.title || ""} onChange={(e) => updateAgenda(i, "title", e.target.value)} className="h-7 text-xs" placeholder="Agenda item" />
                  <Input type="number" value={a.duration_minutes || ""} onChange={(e) => updateAgenda(i, "duration_minutes", e.target.value ? Number(e.target.value) : null)} className="h-7 w-16 text-xs" placeholder="min" />
                  <Button size="sm" variant="ghost" type="button" onClick={() => removeAgenda(i)} className="rounded-none h-7 px-1">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
              {form.agenda.length === 0 && <p className="text-[11px] text-muted-foreground">No agenda items yet. Click + Item to add one.</p>}
            </div>
          </div>

          <div>
            <div className="label-eyebrow mb-1 inline-flex items-center gap-1"><Repeat className="w-3 h-3" /> Recurrence (optional)</div>
            <div className="grid grid-cols-2 gap-2">
              <Select value={form.recurrence_freq || "_none"} onValueChange={(v) => set("recurrence_freq", v === "_none" ? "" : v)}>
                <SelectTrigger className="h-9 text-xs" data-testid="meeting-new-recurrence"><SelectValue placeholder="No recurrence" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">— No recurrence —</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Bi-weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
              {form.recurrence_freq && (
                <Input type="number" min={1} max={52} value={form.recurrence_count} onChange={(e) => set("recurrence_count", e.target.value)} placeholder="Occurrences" data-testid="meeting-new-recurrence-count" />
              )}
            </div>
            {form.recurrence_freq && <p className="text-[10px] text-muted-foreground mt-1">We&apos;ll create {form.recurrence_count} occurrences total. Each one has its own agenda, notes, and action items.</p>}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="meeting-new-submit">{busy ? "Saving…" : "Schedule"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MeetingDetailDialog({ meetingId, projectMembers, canUpdate, canDelete, canCreateTask, canUpdateTask, onClose }) {
  const { activeWorkspaceId } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(true);
  const [notes, setNotes] = useState("");
  const [aiRunning, setAiRunning] = useState(false);
  const [wsMembers, setWsMembers] = useState([]);
  const [emailOpen, setEmailOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/meetings/${meetingId}`);
      setData(data);
      setNotes(data.meeting.discussion_notes || "");
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load meeting")); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [meetingId]);

  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/members`).then((r) => setWsMembers(r.data || [])).catch(() => {});
  }, [activeWorkspaceId]);

  const memberList = useMemo(() => {
    if (projectMembers && projectMembers.length > 0) {
      return projectMembers.map((m) => ({ user_id: m.user_id, name: m.user?.name, email: m.user?.email }));
    }
    return wsMembers.map((m) => ({ user_id: m.user_id || m.id, name: m.name, email: m.email }));
  }, [projectMembers, wsMembers]);

  const patchMeeting = async (patch) => {
    try { await api.patch(`/meetings/${meetingId}`, patch); await load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };
  const updateAgendaStatus = async (idx, status) => {
    const next = (data.meeting.agenda || []).map((a, i) => i === idx ? { ...a, status } : a);
    await patchMeeting({ agenda: next });
  };
  const removeAgenda = async (idx) => {
    const next = (data.meeting.agenda || []).filter((_, i) => i !== idx);
    await patchMeeting({ agenda: next });
  };
  const addAgenda = async ({ title, duration_minutes }) => {
    if (!title.trim()) return;
    const next = [...(data.meeting.agenda || []), { title: title.trim(), duration_minutes: duration_minutes || null, status: "pending" }];
    await patchMeeting({ agenda: next });
    toast.success("Agenda item added");
  };
  const saveNotes = async () => {
    await patchMeeting({ discussion_notes: notes });
    toast.success("Notes saved");
  };
  const remove = async (alsoSeries = false) => {
    if (!window.confirm(alsoSeries ? "Delete the whole series?" : "Delete this meeting?")) return;
    try {
      await api.delete(`/meetings/${meetingId}${alsoSeries ? "?series=true" : ""}`);
      toast.success("Deleted");
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };
  const exportPdf = async () => {
    try {
      const res = await api.get(`/meetings/${meetingId}/mom-pdf`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `MoM_${(data.meeting.title || "meeting").replace(/[^a-z0-9]+/gi, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErrorMessage(e, "PDF export failed")); }
  };

  const summarize = async () => {
    setAiRunning(true);
    try {
      const { data: s } = await api.post(`/meetings/${meetingId}/summarize`, { extract_action_items: true });
      toast.success(`AI summary ready · ${s.action_items_created?.length || 0} action items extracted`);
      await load();
    } catch (e) { toast.error(apiErrorMessage(e, "AI summarize failed")); }
    finally { setAiRunning(false); }
  };

  if (busy || !data) {
    return (
      <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
        <DialogContent><DialogHeader><DialogTitle>Loading meeting…</DialogTitle></DialogHeader></DialogContent>
      </Dialog>
    );
  }
  const m = data.meeting;
  const ais = data.action_items || [];

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] flex flex-col" data-testid="meeting-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 truncate">
            {m.is_external ? <Globe className="w-4 h-4 text-emerald-500" /> : <Lock className="w-4 h-4 text-muted-foreground" />}
            <span className="truncate">{m.title}</span>
            {m.series_id && <span className="chip text-[10px] inline-flex items-center"><Repeat className="w-3 h-3 mr-1" /> {m.series_index === 0 ? "series" : `#${m.series_index + 1}`}</span>}
          </DialogTitle>
          <DialogDescription className="text-xs">
            <Calendar className="w-3 h-3 inline mr-1" /> {new Date(m.scheduled_at).toLocaleString()} · {m.duration_minutes} min
            {m.location ? <> · <MapPin className="w-3 h-3 inline" /> {m.location}</> : null}
            {m.video_url ? <> · <a href={m.video_url} target="_blank" rel="noopener noreferrer" className="text-primary underline">join</a></> : null}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-4 pr-1">
          {/* Status + MoM actions */}
          <div className="flex items-center gap-2 flex-wrap">
            {canUpdate && (
              <>
                <span className="label-eyebrow">Status</span>
                <Select value={m.status || "scheduled"} onValueChange={(v) => patchMeeting({ status: v })}>
                  <SelectTrigger className="h-7 text-xs w-40" data-testid="meeting-detail-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_MEETING.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                </Select>
              </>
            )}
            <div className="ml-auto flex items-center gap-1">
              <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px]" onClick={exportPdf} data-testid="meeting-detail-mom-pdf">
                <Download className="w-3 h-3 mr-1" /> Download MoM
              </Button>
              <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px]" onClick={() => setEmailOpen(true)} data-testid="meeting-detail-mom-email">
                <Mail className="w-3 h-3 mr-1" /> Email MoM
              </Button>
              <Button size="sm" variant="ghost" className="rounded-none h-7 text-[10px]" onClick={() => setLogOpen(true)} data-testid="meeting-detail-mom-log">
                <History className="w-3 h-3" />
              </Button>
            </div>
          </div>

          {/* Agenda */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="label-eyebrow inline-flex items-center gap-1"><ClipboardList className="w-3 h-3" /> Agenda · {(m.agenda || []).length}</div>
              {canUpdate && <AgendaAddInline onAdd={addAgenda} />}
            </div>
            <ul className="border border-border divide-y divide-border" data-testid="meeting-agenda">
              {(m.agenda || []).map((a, i) => (
                <li key={a.id || i} className="px-3 py-2 flex items-center gap-2 text-sm" data-testid={`meeting-agenda-${i}`}>
                  <span className="flex-1 truncate">{a.title}</span>
                  {a.duration_minutes && <span className="text-xs text-muted-foreground">{a.duration_minutes}m</span>}
                  {canUpdate ? (
                    <>
                      <Select value={a.status || "pending"} onValueChange={(v) => updateAgendaStatus(i, v)}>
                        <SelectTrigger className="h-6 text-[10px] w-28" data-testid={`meeting-agenda-status-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUS_AGENDA.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                      <button type="button" onClick={() => removeAgenda(i)} className="text-meta hover:text-destructive" data-testid={`meeting-agenda-del-${i}`}>
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </>
                  ) : <span className="chip text-[10px]">{a.status}</span>}
                </li>
              ))}
              {(!m.agenda || m.agenda.length === 0) && <li className="px-3 py-2 text-xs text-muted-foreground">No agenda items yet. Use + Item to add one.</li>}
            </ul>
          </div>

          {/* Discussion notes + AI */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="label-eyebrow inline-flex items-center gap-1"><FileText className="w-3 h-3" /> Discussion notes</div>
              {canUpdate && (
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px]" onClick={saveNotes} data-testid="meeting-detail-save-notes">Save notes</Button>
                  <Button size="sm" className="rounded-none h-7 text-[10px]" onClick={summarize} disabled={aiRunning} data-testid="meeting-detail-summarize">
                    {aiRunning ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Wand2 className="w-3 h-3 mr-1" />}
                    {aiRunning ? "Summarising…" : "AI summarise"}
                  </Button>
                </div>
              )}
            </div>
            <SmartRichText
              value={notes}
              onChange={setNotes}
              minRows={5}
              maxHeight={420}
              mediaMode="url-only"
              workspaceId={activeWorkspaceId}
              placeholder="Capture decisions, debate, side notes. Click 'AI summarise' to auto-extract a recap + action items."
              testid="meeting-detail-notes"
            />
          </div>

          {/* AI summary block */}
          {m.ai_summary && (
            <div className="border-l-2 border-primary bg-primary/5 p-3 text-xs space-y-2" data-testid="meeting-ai-summary">
              <div className="label-eyebrow inline-flex items-center gap-1 text-primary"><Sparkles className="w-3 h-3" /> AI summary</div>
              <p className="text-sm">{m.ai_summary.summary}</p>
              {m.ai_summary.decisions?.length > 0 && (
                <>
                  <div className="label-eyebrow mt-2">Decisions</div>
                  <ul className="list-disc list-inside text-sm">{m.ai_summary.decisions.map((d, i) => <li key={i}>{d}</li>)}</ul>
                </>
              )}
            </div>
          )}

          {/* Action items */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="label-eyebrow inline-flex items-center gap-1"><ListChecks className="w-3 h-3" /> Action items · {ais.length}</div>
              {canCreateTask && (
                <ActionItemAdd meetingId={meetingId} projectMembers={memberList} onSaved={load} />
              )}
            </div>
            <ul className="border border-border divide-y divide-border" data-testid="meeting-action-items">
              {ais.map((t) => (
                <li key={t.id} className="px-3 py-2 grid grid-cols-12 gap-2 items-center text-sm" data-testid={`meeting-action-item-${t.id}`}>
                  <div className="col-span-6 min-w-0">
                    <div className="font-medium truncate">{t.title}</div>
                    {t.description && <div className="text-xs text-muted-foreground truncate">{t.description}</div>}
                  </div>
                  <div className="col-span-2 text-xs">
                    {canUpdateTask ? (
                      <Select value={t.assignee_id || "_none"} onValueChange={(v) => api.patch(`/tasks/${t.id}`, { assignee_id: v === "_none" ? null : v }).then(load)}>
                        <SelectTrigger className="h-7 text-[10px]" data-testid={`ai-assignee-${t.id}`}><SelectValue placeholder="Unassigned" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">Unassigned</SelectItem>
                          {memberList.map((mem) => <SelectItem key={mem.user_id} value={mem.user_id}>{mem.name || mem.email}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : <span>{t.assignee_id || "—"}</span>}
                  </div>
                  <div className="col-span-2">
                    {canUpdateTask ? (
                      <Input type="date" defaultValue={t.due_date?.slice(0,10) || ""} onBlur={(e) => api.patch(`/tasks/${t.id}`, { due_date: e.target.value || null }).then(load)} className="h-7 text-xs" data-testid={`ai-due-${t.id}`} />
                    ) : <span className="text-xs">{t.due_date || "—"}</span>}
                  </div>
                  <div className="col-span-2">
                    {canUpdateTask ? (
                      <Select value={t.status} onValueChange={(v) => api.patch(`/tasks/${t.id}`, { status: v }).then(load)}>
                        <SelectTrigger className="h-7 text-[10px]" data-testid={`ai-status-${t.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{TASK_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : <span className="chip text-[10px]">{t.status}</span>}
                  </div>
                </li>
              ))}
              {ais.length === 0 && <li className="px-3 py-2 text-xs text-muted-foreground">No action items yet.</li>}
            </ul>
          </div>
        </div>

        <DialogFooter className="flex items-center gap-2">
          {canDelete && (
            <>
              <Button variant="ghost" className="rounded-none text-red-500 hover:text-red-600 mr-auto" onClick={() => remove(false)} data-testid="meeting-detail-delete">
                <Trash2 className="w-3 h-3 mr-1" /> Delete
              </Button>
              {m.series_id && (
                <Button variant="ghost" className="rounded-none text-red-500 hover:text-red-600 text-xs" onClick={() => remove(true)} data-testid="meeting-detail-delete-series">
                  Delete whole series
                </Button>
              )}
            </>
          )}
          <Button variant="outline" className="rounded-none" onClick={onClose} data-testid="meeting-detail-close">Close</Button>
        </DialogFooter>
      </DialogContent>
      {emailOpen && (
        <EmailMomDialog meetingId={meetingId} meeting={m} memberList={memberList} onClose={() => setEmailOpen(false)} />
      )}
      {logOpen && (
        <MomLogDialog meetingId={meetingId} onClose={() => setLogOpen(false)} />
      )}
    </Dialog>
  );
}

function AgendaAddInline({ onAdd }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [dur, setDur] = useState("");
  if (!open) {
    return <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px]" onClick={() => setOpen(true)} data-testid="meeting-agenda-add-btn"><Plus className="w-3 h-3 mr-1" /> Item</Button>;
  }
  return (
    <form className="flex items-center gap-1"
          onSubmit={async (e) => {
            e.preventDefault();
            if (!title.trim()) return;
            await onAdd({ title, duration_minutes: dur ? Number(dur) : null });
            setTitle(""); setDur(""); setOpen(false);
          }}>
      <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Agenda topic…" className="h-7 w-56 text-xs" autoFocus data-testid="meeting-agenda-add-title" />
      <Input value={dur} onChange={(e) => setDur(e.target.value)} placeholder="min" type="number" className="h-7 w-16 text-xs" data-testid="meeting-agenda-add-dur" />
      <Button type="submit" size="sm" className="rounded-none h-7 text-[10px]" data-testid="meeting-agenda-add-submit">Add</Button>
      <Button type="button" size="sm" variant="ghost" className="rounded-none h-7 text-[10px]" onClick={() => setOpen(false)}>×</Button>
    </form>
  );
}

function ActionItemAdd({ meetingId, projectMembers, onSaved }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [assignee, setAssignee] = useState("");
  const [due, setDue] = useState("");
  const [priority, setPriority] = useState("medium");
  const [isExt, setIsExt] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!title.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      await api.post(`/meetings/${meetingId}/action-items`, {
        title, assignee_id: assignee || null, due_date: due || null,
        priority, is_external: isExt,
      });
      toast.success("Action item added");
      setOpen(false);
      setTitle(""); setAssignee(""); setDue(""); setPriority("medium"); setIsExt(false);
      onSaved && onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Couldn't add")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-none h-7 text-[10px]" data-testid="ai-add-btn"><Plus className="w-3 h-3 mr-1" /> Action item</Button>
      </DialogTrigger>
      <DialogContent data-testid="ai-add-dialog">
        <DialogHeader><DialogTitle>New action item</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What needs to be done?" data-testid="ai-add-title" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="label-eyebrow mb-1">Assignee</div>
              <Select value={assignee || "_none"} onValueChange={(v) => setAssignee(v === "_none" ? "" : v)}>
                <SelectTrigger className="h-9 text-sm" data-testid="ai-add-assignee"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">Unassigned</SelectItem>
                  {projectMembers.map((m) => {
                    // Accept either flat {user_id,name,email} or nested {user:{...}}
                    const uid = m.user_id || m.id;
                    const label = m.name || m.email || m.user?.name || m.user?.email || uid;
                    return <SelectItem key={uid} value={uid}>{label}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="label-eyebrow mb-1">Due</div>
              <Input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="ai-add-due" />
            </div>
            <div>
              <div className="label-eyebrow mb-1">Priority</div>
              <Select value={priority} onValueChange={setPriority}>
                <SelectTrigger className="h-9 text-sm" data-testid="ai-add-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 px-3 mt-5 border border-border text-xs cursor-pointer">
              <Checkbox checked={isExt} onCheckedChange={(v) => setIsExt(!!v)} data-testid="ai-add-external" />
              External action
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
          <Button className="rounded-none" onClick={submit} disabled={busy} data-testid="ai-add-submit">{busy ? "Saving…" : "Add"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
